/**
 * msi-corp_app-integrations.js
 * ---------------------------------------------------------------------------
 * 第三方 App 整合的**宣告式**啟動器（唯一的進入點）。
 *
 * 掃描頁面上所有 `<script data-msi-app="<id>" data-*>` 設定標籤，
 * 依屬性組出就緒訊號與（可選的）元素守門員，再登記到全站註冊中心。
 *
 * 【要新增一個 App，只需要在 Liquid 加一個標籤，不必寫任何 JS】
 *
 *   <script type="application/msi-app"
 *           data-msi-app="foo"
 *           data-ready-network="foo-app.com"
 *           data-guard-selector=".add-to-cart"></script>
 *
 *   （`type="application/msi-app"` 是瀏覽器不會執行的類型，純設定用，
 *     與載入本檔的 `<script type="module" src="...">` 標籤是各自獨立的兩個標籤。）
 *
 * 【只想等 App 跑完再做事、不需要鎖任何元素】
 *   省略 `data-guard-selector` 即可，之後：
 *     const result = await window.MSI.apps.ready('foo');   // { reason, elapsedMs, ... }
 *     document.addEventListener('msi:app-ready:foo', (e) => e.detail);
 *
 * 【診斷】
 *   網址加 `?msi_debug=1`（全部 App）或 `?msi_<id>_debug=1`（單一 App）開啟日誌。
 *   `window.MSI.apps.diagnostics()` 取得所有 App 的狀態快照。
 *   `window.MSI.apps.get('<id>').logger.dump()` 取回完整軌跡（未開 debug 時仍有記錄）。
 *
 * 【GDPR】不讀寫 cookie / storage，不蒐集也不傳送任何個人資料。
 * ---------------------------------------------------------------------------
 */

import { createLogger, resolveDebugFlag } from './msi-corp_debug-logger.js';
import { createElementGuard, GUARD_DEFAULTS } from './msi-corp_element-guard.js';
import {
  detectAppReady,
  elementSignal,
  eventSignal,
  globalSignal,
  networkIdleSignal,
  registerApp,
} from './msi-corp_app-ready.js';

const CONFIG_SELECTOR = 'script[data-msi-app]';
const GLOBAL_DEBUG_PARAM = 'msi_debug';

function toPositiveInt(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * 把一個設定標籤的 `data-*` 讀成設定物件。
 * @param {HTMLScriptElement} host
 */
export function readAppConfig(host) {
  const data = host.dataset;
  const appId = (data.msiApp || '').trim();

  return {
    appId,
    ready: {
      networkPattern: data.readyNetwork || '',
      quietPeriodMs: toPositiveInt(data.readyQuietMs, 1200),
      globalPath: data.readyGlobal || '',
      globalPollMs: toPositiveInt(data.readyGlobalPollMs, 100),
      elementSelector: data.readyElement || '',
      eventType: data.readyEvent || '',
      mode: data.readyMode === 'all' ? 'all' : 'any',
      maxWaitMs: toPositiveInt(data.maxWaitMs, 8000),
    },
    guard: {
      selector: data.guardSelector || '',
      lockUntilReady: data.lockUntilReady !== 'false',
      enforceMs: toPositiveInt(data.enforceMs, GUARD_DEFAULTS.enforceMs),
      showSpinner: data.spinner !== 'false',
      runawayLimit: toPositiveInt(data.runawayLimit, GUARD_DEFAULTS.runawayLimit),
      runawayWindowMs: toPositiveInt(data.runawayWindowMs, GUARD_DEFAULTS.runawayWindowMs),
      fallbackIntervalMs: toPositiveInt(data.fallbackIntervalMs, GUARD_DEFAULTS.fallbackIntervalMs),
      lockedMessage: data.lockedMessage || GUARD_DEFAULTS.lockedMessage,
      readyMessage: data.readyMessage || GUARD_DEFAULTS.readyMessage,
    },
    debug: resolveDebugFlag([GLOBAL_DEBUG_PARAM, `msi_${appId}_debug`], data.debug === 'true'),
  };
}

/** 依設定組出就緒訊號清單。沒有任何訊號時 detectAppReady 會立即放行。 */
function buildSignals(readyConfig) {
  const signals = [];
  if (readyConfig.networkPattern) {
    signals.push(
      networkIdleSignal({ pattern: readyConfig.networkPattern, quietPeriodMs: readyConfig.quietPeriodMs }),
    );
  }
  if (readyConfig.globalPath) {
    signals.push(globalSignal({ path: readyConfig.globalPath, pollMs: readyConfig.globalPollMs }));
  }
  if (readyConfig.elementSelector) signals.push(elementSignal({ selector: readyConfig.elementSelector }));
  if (readyConfig.eventType) signals.push(eventSignal({ type: readyConfig.eventType }));
  return signals;
}

/**
 * 啟動單一 App 的整合。
 * @param {ReturnType<typeof readAppConfig>} config
 */
export function startIntegration(config) {
  const logger = createLogger({ namespace: `MSI/${config.appId}`, enabled: config.debug });
  logger.phase('boot', config);

  const guard = config.guard.selector ? createElementGuard(config.guard, logger, config.appId) : null;

  if (guard && config.guard.lockUntilReady) guard.lock();
  else if (guard) logger.info('lockUntilReady=false，就緒前不接管鎖定');

  const ready = detectAppReady(
    { appId: config.appId, signals: buildSignals(config.ready), mode: config.ready.mode, maxWaitMs: config.ready.maxWaitMs },
    logger,
  ).then((result) => {
    // 無論偵測成功或逾時都要解鎖 —— fail-open 是本模組的硬性契約。
    guard?.release(result.reason);
    return result;
  });

  return registerApp({
    id: config.appId,
    ready,
    guard,
    logger,
    config,
  });
}

/** 掃描並啟動頁面上所有設定標籤。 */
export function bootstrap(root = document) {
  const records = [];
  for (const host of root.querySelectorAll(CONFIG_SELECTOR)) {
    const config = readAppConfig(host);
    if (!config.appId) {
      console.warn('[MSI] 設定標籤缺少 data-msi-app（App 識別碼），已略過', host);
      continue;
    }
    records.push(startIntegration(config));
  }
  return records;
}

export default bootstrap();
